import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import PremiumFeatureGate from "../PremiumFeatureGate";

const TOTAL_SEATS = 5;
const ROLES = ["Administrator", "Editor", "Viewer"];

const ROLE_STYLES = {
  Administrator: "bg-grey-900 text-white",
  Editor:        "bg-blue-100 text-blue-700",
  Viewer:        "bg-grey-100 text-grey-600",
};

const initials = (name = "") =>
  name.trim().split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("");

const avatarColor = (name = "") => {
  const hues = [220, 150, 30, 290, 340, 180, 15];
  let h = 0;
  for (let i = 0; i < name.length; i++) h += name.charCodeAt(i);
  return `hsl(${hues[h % hues.length]}, 60%, 50%)`;
};

const DEFAULT_CONTRIBUTORS = [
  { id: "c2", name: "Sarah Johnson", email: "sarah@yourco.com",  role: "Editor",  status: "active"  },
  { id: "c3", name: "Mike Chen",     email: "mike@yourco.com",   role: "Editor",  status: "active"  },
  { id: "c4", name: "Priya Nair",    email: "priya@yourco.com",  role: "Viewer",  status: "pending" },
];

// ── Role dropdown ──────────────────────────────────────────────────────────────
function RoleDropdown({ role, onChange, isOwner }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (isOwner) {
    return (
      <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${ROLE_STYLES.Administrator}`}>
        Administrator
      </span>
    );
  }

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors hover:opacity-80 ${ROLE_STYLES[role] || ROLE_STYLES.Viewer}`}
      >
        {role}
        <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-grey-200 rounded-xl shadow-lg z-20 overflow-hidden">
          <p className="px-3 pt-2.5 pb-1 text-xs font-semibold text-grey-400 uppercase tracking-wide">
            Access Level
          </p>
          {ROLES.map(r => (
            <button
              key={r}
              onClick={() => { onChange(r); setOpen(false); }}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-grey-50 transition-colors ${
                r === role ? "font-semibold text-grey-900" : "text-grey-600"
              }`}
            >
              {r}
              {r === role && (
                <svg className="w-3.5 h-3.5 text-grey-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
          <div className="border-t border-grey-100 mt-1 mb-1" />
          <div className="px-3 pb-2.5">
            <p className="text-xs text-grey-400 leading-relaxed">
              <strong className="text-grey-600">Administrator</strong> — full account access<br />
              <strong className="text-grey-600">Editor</strong> — manage vendors & docs<br />
              <strong className="text-grey-600">Viewer</strong> — read-only access
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Remove confirmation ────────────────────────────────────────────────────────
function RemoveButton({ member, onConfirm }) {
  const [confirming, setConfirming] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!confirming) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setConfirming(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [confirming]);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setConfirming(true)}
        className="p-1.5 text-grey-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
        title="Remove member"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {confirming && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-grey-200 rounded-xl shadow-lg z-20 p-3">
          <p className="text-xs font-semibold text-grey-900 mb-0.5">Remove {member.name}?</p>
          <p className="text-xs text-grey-400 mb-3">They'll lose access immediately.</p>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirming(false)}
              className="flex-1 px-2 py-1.5 text-xs font-semibold text-grey-600 border border-grey-200 rounded-lg hover:bg-grey-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => { onConfirm(); setConfirming(false); }}
              className="flex-1 px-2 py-1.5 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
function ContributorsContent({ userDetails }) {
  useAuth();
  const [contributors, setContributors] = useState(DEFAULT_CONTRIBUTORS);
  const [showInviteBanner, setShowInviteBanner] = useState(false);

  const owner = {
    id:     "owner",
    name:   userDetails?.fullName  || "You",
    email:  userDetails?.userEmail || "",
    role:   "Administrator",
    status: "active",
  };

  const members   = [owner, ...contributors];
  const usedSeats = members.length;
  const freeSeats = TOTAL_SEATS - usedSeats;

  const handleRemove = (id) =>
    setContributors(prev => prev.filter(c => c.id !== id));

  const handleRoleChange = (id, newRole) =>
    setContributors(prev => prev.map(c => c.id === id ? { ...c, role: newRole } : c));

  const handleInviteClick = () => {
    setShowInviteBanner(true);
    setTimeout(() => setShowInviteBanner(false), 4000);
  };

  return (
    <PremiumFeatureGate featureName="Team Seats">
      <div className="w-full max-w-2xl">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h3 className="text-base font-semibold text-grey-900">Team Members</h3>
            <p className="text-sm text-grey-400 mt-0.5">{usedSeats} of {TOTAL_SEATS} seats used</p>
          </div>
          <button
            onClick={handleInviteClick}
            disabled={freeSeats === 0}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl transition-colors shadow-sm ${
              freeSeats > 0
                ? "bg-grey-900 hover:bg-grey-700 text-white"
                : "bg-grey-100 text-grey-400 cursor-not-allowed"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Invite Member
          </button>
        </div>

        {/* Coming-soon banner */}
        {showInviteBanner && (
          <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl mb-4 text-sm text-blue-700 font-medium">
            <svg className="w-4 h-4 flex-shrink-0 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Contributor invites are coming soon — we'll notify you when they're ready.
          </div>
        )}

        {/* Seat usage bar */}
        <div className="flex items-center gap-1.5 mb-5">
          {Array.from({ length: TOTAL_SEATS }, (_, i) => (
            <div key={i} className={`flex-1 h-1.5 rounded-full transition-colors ${i < usedSeats ? "bg-grey-900" : "bg-grey-200"}`} />
          ))}
          <span className="text-xs text-grey-400 ml-1 whitespace-nowrap">{freeSeats} open</span>
        </div>

        {/* Members list */}
        <div className="space-y-2">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-grey-200 hover:border-grey-300 transition-colors"
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                style={{ backgroundColor: member.id === "owner" ? "#111827" : avatarColor(member.name) }}
              >
                {initials(member.name)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-grey-900 truncate">{member.name}</p>
                  {member.id === "owner" && (
                    <span className="text-xs text-grey-400">(you)</span>
                  )}
                  {member.status === "pending" && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-600 text-xs font-semibold">
                      Pending
                    </span>
                  )}
                </div>
                <p className="text-xs text-grey-400 truncate">{member.email}</p>
              </div>

              <RoleDropdown
                role={member.role}
                isOwner={member.id === "owner"}
                onChange={(r) => handleRoleChange(member.id, r)}
              />

              {member.id !== "owner" && (
                <RemoveButton
                  member={member}
                  onConfirm={() => handleRemove(member.id)}
                />
              )}
            </div>
          ))}

          {/* Empty seat slots */}
          {Array.from({ length: freeSeats }, (_, i) => (
            <div key={`empty-${i}`} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-grey-200">
              <div className="w-9 h-9 rounded-full bg-grey-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-grey-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <p className="text-sm text-grey-300 font-medium">Open seat</p>
            </div>
          ))}
        </div>

        <p className="text-xs text-grey-400 mt-4">
          <strong className="text-grey-500">Administrator</strong> — full account access, same as you.{" "}
          <strong className="text-grey-500">Editor</strong> — manage vendors, documents, and announcements.{" "}
          <strong className="text-grey-500">Viewer</strong> — read-only access to events and registrations.
        </p>
      </div>
    </PremiumFeatureGate>
  );
}

export default ContributorsContent;
